import { intervalToDuration } from 'date-fns';

import { SymbolLivePnlItem } from '@flipster/apis/v1/community/types';
import { OrderSideBadge } from '@flipster/features/src/misc/badge';
import { UserTierAccountIcon } from '@flipster/features/src/shared/community-feed/user-tier-account-icon';
import { Pnl } from '@flipster/features/src/shared/pnl';
import { FormattedMessage } from '@flipster/i18n';
import { StatusBadge } from '@flipster/react/design-system/status-badge';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { timestampToDate } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

interface LivePnlShoutoutCardProps {
  item: SymbolLivePnlItem;
}

export const LivePnlShoutoutCard = ({ item }: LivePnlShoutoutCardProps) => {
  const closedDuration = (() => {
    if (!item.closedAt) return;
    const { seconds, minutes } = intervalToDuration({ start: timestampToDate(item.closedAt), end: Date.now() });
    return minutes ? `${minutes}m` : `${seconds ?? 1}s`;
  })();
  return (
    <div
      className={hstack({
        py: 16,
        px: 12,
        gap: 12,
        bg: 'background.default.level-1',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        rounded: 4,
      })}
      onClick={() =>
        segment.track(CommunityEvent.TopLivePnlCardClick, {
          symbol: item.symbol,
          profile_id: item.profileId,
          profile_username: item.nickname,
          unrealized_pnl_percentage: Number(item.pnlPct),
          unrealized_pnl: Number(item.pnl),
          side: item.side,
        })
      }
    >
      <UserTierAccountIcon size={32} vipTier={item.vipTier} profileImageUrl={item.profileImageUrl} />
      <div className={hstack({ gap: 8, justify: 'space-between', flex: 1 })}>
        <div className={stack({ gap: 2, justify: 'center', minW: 0 })}>
          <p
            className={css({
              textStyle: 'Text/L/Strong',
              color: 'content.default.level-3',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            })}
          >
            {item.nickname}
          </p>
          <div className={hstack({ gap: 5 })}>
            {item.closedAt ? (
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage
                  id='Autogen_common.r251126.s_Closed_closedDuration_ago'
                  values={{
                    closedDuration: closedDuration,
                  }}
                />
              </p>
            ) : (
              <>
                <StatusBadge color='green' positionType='normal' />
                <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                  <FormattedMessage id='Autogen_common.r251126.s_Active' />
                </span>
              </>
            )}
          </div>
        </div>
        <div className={stack({ gap: 4, align: 'flex-end', flexShrink: 0 })}>
          <Pnl number={item.pnl} percent={item.pnlPct}>
            <Pnl.Number /> <Pnl.Percent />
          </Pnl>
          <OrderSideBadge orderSide={item.side} />
        </div>
      </div>
    </div>
  );
};
